import React, { useState, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";

import Icon from "@mdi/react";
import { mdiCheckCircleOutline } from "@mdi/js";

import "../pages/header.css";
import "../pages/Uploads.css";

import logotypeDark from "../assets/dance_locale_3-200h.png";
import danceLocaleIcon from "../assets/logo_fox_map_pin_v4-600h.png";
import { authApi } from "../api/auth";

/**
 * Email verification landing page (REG-4). Public: reached from the emailed
 * verification link (/verify-email/:token) or directly with no token
 * (/verify-email) as the recovery surface for a user whose email never arrived.
 *
 * Layout mirrors ResetPassword.js (the canonical public auth page).
 */
const VerifyEmail = () => {
    const { token } = useParams();

    // verifying | verified | already | needs-link
    const [status, setStatus] = useState(token ? "verifying" : "needs-link");
    // Error shown in the needs-link state; null for a direct (tokenless) visit,
    // which is a friendly "request a link" page rather than an error page.
    const [errorMessage, setErrorMessage] = useState(null);

    const [email, setEmail] = useState("");
    const [isSending, setIsSending] = useState(false);
    const [resendDone, setResendDone] = useState(false);
    const [resendMessage, setResendMessage] = useState("");

    // React 18 StrictMode double-invokes mount effects in dev; a second POST would
    // resolve alreadyVerified:true and flip a genuinely fresh verification into the
    // "already verified" copy. The ref guard ensures exactly one confirm call.
    const hasRun = useRef(false);
    useEffect(() => {
        if (hasRun.current) return;
        hasRun.current = true;

        if (!token) return; // direct visit: stay on the request-a-link state

        authApi
            .verifyEmail(token)
            .then(res => {
                if (res.ok && res.success) {
                    setStatus(res.alreadyVerified ? "already" : "verified");
                } else {
                    // res.code is TOKEN_EXPIRED / TOKEN_INVALID; a Joi-shaped rejection
                    // (malformed token in the URL) has no flat message, so fall back.
                    setErrorMessage(res.message || "This verification link is not valid. Request a new one below.");
                    setStatus("needs-link");
                }
            })
            .catch(() => {
                setErrorMessage("Something went wrong confirming this link. Please try again, or request a new one below.");
                setStatus("needs-link");
            });
    }, [token]);

    const handleResend = async e => {
        e.preventDefault();
        setIsSending(true);
        try {
            const result = await authApi.resendVerification(email.trim());
            setResendMessage(result.message || "If that address has an unverified Dance Localé account, a new verification link is on its way.");
            setResendDone(true);
        } catch (error) {
            console.error("Resend verification failed:", error);
            toast.error("Something went wrong. Please try again.");
        } finally {
            setIsSending(false);
        }
    };

    const isSuccess = status === "verified" || status === "already";

    const pageTitle = {
        verifying: "Confirming your email address",
        verified: "Email Verified!",
        already: "Already Verified",
        "needs-link": errorMessage ? "Confirm your email address" : "Need a verification link?",
    }[status];

    const pageDescription = {
        verifying: "One moment while we confirm this link.",
        verified: "Thanks for confirming your email address. Your account is all set.",
        already: "This email address is already verified. Your account is all set.",
        "needs-link": errorMessage
            ? "You can request a fresh verification link below."
            : "Enter the email address you registered with and we will send you a new verification link.",
    }[status];

    return (
        <div className="dashboard-container branded-bg">
            {/* Header */}
            <div data-role="Header" className="home-header-container">
                <header className="home-header">
                    <a href="/" className="home-logo1">
                        <img alt="Dance Locale" src={logotypeDark} className="home-image1" />
                        <span className="nav-wordmark">Dance Localé</span>
                    </a>
                    <div className="home-menu1">
                        <a href="/" className="home-text10" style={{ cursor: "pointer", textDecoration: "none" }}>
                            Home
                        </a>
                        <a href="/faq" className="home-text11" style={{ cursor: "pointer", textDecoration: "none" }}>
                            FAQ
                        </a>
                    </div>
                </header>
            </div>

            <div className="dashboard-main">
                <div className="dashboard-content-wrapper" style={{ maxWidth: "500px" }}>
                    <div className="dashboard-header-card">
                        <div style={{ marginBottom: "var(--dl-layout-space-doubleunit)" }}>
                            {/* display:block + margin auto centres reliably (same fix as the
                                deletion pages — textAlign on the wrapper was not taking effect). */}
                            {isSuccess ? (
                                <Icon
                                    path={mdiCheckCircleOutline}
                                    size={2.5}
                                    color="var(--dl-color-green-500)"
                                    style={{ display: "block", margin: "0 auto" }}
                                />
                            ) : (
                                <img
                                    src={danceLocaleIcon}
                                    alt="Dance Locale"
                                    style={{ width: "60px", height: "60px", display: "block", margin: "0 auto" }}
                                />
                            )}
                        </div>

                        <h1
                            className="admin-console-title"
                            style={isSuccess ? { color: "var(--dl-color-green-500)" } : undefined}
                        >
                            {pageTitle}
                        </h1>
                        <p className="admin-console-description">{pageDescription}</p>

                        {isSuccess && (
                            <a
                                href="/"
                                className="btn-purple"
                                style={{
                                    width: "100%",
                                    textDecoration: "none",
                                    display: "block",
                                    textAlign: "center",
                                    boxSizing: "border-box",
                                    padding: "14px 16px",
                                    fontSize: "16px",
                                }}
                            >
                                Continue to Dance Locale
                            </a>
                        )}

                        {status === "needs-link" && (
                            <div>
                                {errorMessage && (
                                    <div
                                        style={{
                                            backgroundColor: "var(--dl-color-danger-50)",
                                            border: "1px solid var(--dl-color-danger-200)",
                                            color: "var(--dl-color-warning-red)",
                                            borderRadius: "8px",
                                            padding: "12px",
                                            fontSize: "14px",
                                            // Match .admin-console-description's 19.2px bottom margin so
                                            // every vertical gap on the card shares one rhythm
                                            // (--dl-layout-space-unit is only 10.24px — visibly uneven).
                                            marginBottom: "calc(24px * var(--scale-factor, 0.8))",
                                        }}
                                    >
                                        {errorMessage}
                                    </div>
                                )}

                                {!resendDone && (
                                    <form onSubmit={handleResend}>
                                        <div className="form-group">
                                            <label className="form-label" htmlFor="email">
                                                Email Address
                                            </label>
                                            <input
                                                type="email"
                                                id="email"
                                                value={email}
                                                onChange={e => setEmail(e.target.value)}
                                                className="form-input"
                                                placeholder="you@example.com"
                                                required
                                                // 16px stops iOS Safari auto-zooming the focused input
                                                // (.form-input alone scales below the 16px threshold)
                                                style={{ fontSize: "16px" }}
                                            />
                                        </div>
                                        <button
                                            type="submit"
                                            className="btn-purple"
                                            disabled={isSending}
                                            style={{
                                                width: "100%",
                                                boxSizing: "border-box",
                                                padding: "14px 16px",
                                                fontSize: "16px",
                                            }}
                                        >
                                            {isSending ? "Sending..." : "Send me a new link"}
                                        </button>
                                    </form>
                                )}

                                {resendDone && (
                                    <div>
                                        <div
                                            style={{
                                                backgroundColor: "var(--dl-color-green-100)",
                                                border: "1px solid var(--dl-color-green-200)",
                                                color: "var(--dl-color-green-700)",
                                                borderRadius: "8px",
                                                padding: "12px",
                                                fontSize: "14px",
                                                marginBottom: "calc(24px * var(--scale-factor, 0.8))",
                                            }}
                                        >
                                            {resendMessage}
                                        </div>
                                        <p
                                            style={{
                                                color: "var(--dl-color-gray-600)",
                                                fontSize: "14px",
                                                // Explicit margins kill the browser-default <p> margins,
                                                // keeping the same gap rhythm as the box above.
                                                margin: "0 0 calc(24px * var(--scale-factor, 0.8))",
                                            }}
                                        >
                                            Not seeing it? Check your spam folder. Still nothing after a few minutes?{" "}
                                            <a
                                                href="/help/contact"
                                                style={{ color: "var(--dl-color-hyperlink)", cursor: "pointer", textDecoration: "underline" }}
                                            >
                                                Contact us
                                            </a>{" "}
                                            and we will sort it out.
                                        </p>
                                    </div>
                                )}

                                <div style={{ textAlign: "center", marginTop: "calc(24px * var(--scale-factor, 0.8))" }}>
                                    <a
                                        href="/"
                                        style={{
                                            color: "var(--dl-color-purple-default)",
                                            cursor: "pointer",
                                            fontSize: "14px",
                                            textDecoration: "underline",
                                        }}
                                    >
                                        Back to Dance Locale
                                    </a>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default VerifyEmail;
